import { useState } from "react";
import { motion } from "framer-motion";
import { Music2, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/**
 * Public suggestion form. Customers propose a song for DJ SPY to
 * consider remixing.  This is NOT a ghost-production service; any
 * selected remix is released under the DJ SPY name.
 *
 * Kept intentionally minimal (song_name + artist + guest email only)
 * per the master prompt.  Reuses the existing api client, toast
 * library, and dark-premium visual language.
 */
const initial = { song_name: "", artist: "", email: "" };

export default function RequestRemix() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading || sent) return;
    if (!form.song_name.trim()) {
      toast.error(t("remixRequest.errorMissingSong"));
      return;
    }
    if (!form.artist.trim()) {
      toast.error(t("remixRequest.errorMissingArtist"));
      return;
    }
    if (!user && !form.email.trim()) {
      toast.error(t("remixRequest.errorMissingEmail"));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        song_name: form.song_name.trim(),
        artist: form.artist.trim(),
      };
      if (!user && form.email.trim()) payload.email = form.email.trim();
      await api.post("/remix-request", payload);
      setSent(true);
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("remixRequest.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="remix-request"
      data-testid="section-remix-request"
      className="relative py-24 md:py-32 px-6 md:px-10 bg-black overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.6]"
        style={{
          background:
            "radial-gradient(circle at 15% 40%, rgba(239,43,43,0.10), transparent 55%), radial-gradient(circle at 90% 60%, rgba(255,255,255,0.03), transparent 55%)",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto grid grid-cols-12 gap-8 md:gap-16">
        <div className="col-span-12 md:col-span-5">
          <p className="font-mono text-xs text-red-brand tracking-[0.3em] uppercase">
            {t("remixRequest.kicker")}
          </p>
          <h2 className="font-anton mt-4 text-5xl md:text-7xl leading-none text-white">
            {t("remixRequest.title")}
          </h2>
          <p className="mt-6 text-white/70 max-w-md leading-relaxed">
            {t("remixRequest.lead")}
          </p>
          <p className="mt-4 text-white/50 max-w-md leading-relaxed text-sm">
            {t("remixRequest.note")}
          </p>
          <p className="mt-4 text-white/40 max-w-md leading-relaxed text-xs font-mono tracking-[0.05em]">
            {t("remixRequest.clarification")}
          </p>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.55 }}
          className="col-span-12 md:col-span-7 hairline rounded-2xl bg-white/[0.02] p-8 md:p-10"
          data-testid="remix-request-form"
        >
          {sent ? (
            <div className="text-center py-6" data-testid="remix-request-success">
              <CheckCircle2 size={44} className="text-red-brand mx-auto" />
              <h3 className="font-anton text-3xl text-white mt-6">
                {t("remixRequest.successTitle")}
              </h3>
              <p className="text-white/70 mt-4 max-w-md mx-auto leading-relaxed">
                {t("remixRequest.successBody")}
              </p>
              <button
                onClick={() => setSent(false)}
                data-testid="remix-request-send-another"
                className="btn-ghost mt-8"
              >
                {t("remixRequest.sendAnother")}
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-1">
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">
                  {t("remixRequest.songLabel")}
                </label>
                <input
                  data-testid="remix-song"
                  name="song_name"
                  required
                  value={form.song_name}
                  onChange={onChange}
                  placeholder={t("remixRequest.songPlaceholder")}
                />
              </div>
              <div className="sm:col-span-1">
                <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">
                  {t("remixRequest.artistLabel")}
                </label>
                <input
                  data-testid="remix-artist"
                  name="artist"
                  required
                  value={form.artist}
                  onChange={onChange}
                  placeholder={t("remixRequest.artistPlaceholder")}
                />
              </div>
              <div className="sm:col-span-2">
                {user ? (
                  <p
                    data-testid="remix-logged-in-hint"
                    className="text-white/50 text-xs font-mono tracking-wide"
                  >
                    {t("remixRequest.loggedInHint")} <span className="text-white" dir="ltr">{user.email}</span>
                  </p>
                ) : (
                  <>
                    <label className="font-mono text-[10px] tracking-[0.3em] text-white/50">
                      {t("remixRequest.emailLabel")}
                    </label>
                    <input
                      data-testid="remix-email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      placeholder={t("remixRequest.emailPlaceholder")}
                      dir="ltr"
                    />
                  </>
                )}
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={loading || sent}
                  data-testid="remix-submit"
                  className="btn-primary w-full sm:w-auto disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      {t("remixRequest.sending")}
                    </>
                  ) : (
                    <>
                      <Music2 size={16} />
                      {t("remixRequest.cta")}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
